import React, { useState } from 'react';
import { X, Send, AlertTriangle, Lightbulb, Bug, HelpCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { feedbackService } from '../../services/feedbackService';
import { FeedbackType } from '../../types/feedback';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEEDBACK_TYPES: { value: FeedbackType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'suggestion', label: 'Suggestion', icon: <Lightbulb className="w-4 h-4" />, color: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  { value: 'bug_report', label: 'Report a Bug', icon: <Bug className="w-4 h-4" />, color: 'text-red-400 bg-red-500/10 border-red-500/30' },
  { value: 'complaint', label: 'Complaint', icon: <AlertTriangle className="w-4 h-4" />, color: 'text-orange-400 bg-orange-500/10 border-orange-500/30' },
  { value: 'other', label: 'Other', icon: <HelpCircle className="w-4 h-4" />, color: 'text-slate-400 bg-slate-500/10 border-slate-500/30' },
];

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('suggestion');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) return;
    setSending(true);
    setError(null);
    try {
      await feedbackService.create({ feedback_type: feedbackType, title: title.trim(), message: message.trim() });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setTitle('');
        setMessage('');
        setFeedbackType('suggestion');
        onClose();
      }, 1500);
    } catch {
      setError('Failed to send feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const canSubmit = title.trim().length > 0 && message.trim().length > 0 && !sending;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-[var(--surface-elevated)] rounded-2xl shadow-2xl border border-[var(--border-color)] w-full max-w-lg overflow-hidden">
        {success ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Thank you!</h3>
            <p className="text-sm text-[var(--text-secondary)] text-center">Your feedback has been submitted. We appreciate it.</p>
          </div>
        ) : (
          <>
            <div className="px-6 pt-6 pb-4 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Send Feedback</h3>
                <p className="text-sm text-[var(--text-secondary)] mt-1">Help us improve your experience</p>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--hover-bg)] transition-colors">
                <X className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
            </div>

            <div className="px-6 pb-2">
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-2 block">Type</label>
              <div className="flex flex-wrap gap-2">
                {FEEDBACK_TYPES.map((ft) => (
                  <button
                    key={ft.value}
                    onClick={() => setFeedbackType(ft.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                      feedbackType === ft.value
                        ? ft.color + ' ring-1 ring-current'
                        : 'text-[var(--text-secondary)] bg-[var(--bg-secondary)] border-[var(--border-color)] hover:border-[var(--hover-border)]'
                    }`}
                  >
                    {ft.icon}
                    {ft.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-6 py-3 space-y-3">
              <div>
                <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1.5 block">Subject</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief summary..."
                  maxLength={255}
                  className="w-full px-3 py-2.5 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] text-sm focus:outline-none focus:border-[var(--border-focus)] transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1.5 block">Details</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us more about your feedback..."
                  rows={4}
                  maxLength={5000}
                  className="w-full px-3 py-2.5 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] text-sm focus:outline-none focus:border-[var(--border-focus)] transition-colors resize-none"
                />
              </div>
            </div>

            {error && (
              <div className="px-6 pb-2">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border-color)] text-[var(--text-primary)] font-medium hover:bg-[var(--hover-bg)] transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
